'use client';

import { PublicReview } from '@shared/types';
import { PublicEducator } from '@cademy-package/sdk-component-types';
import Container from '../../../../components/Container';
import { EducatorProfile, EducatorProfileProps } from '../../../../components/EducatorProfile';
import styles from './styles.module.scss';
import {
    GetEducatorReviewsResult,
    getEducatorReviews,
} from '../../../../pages_components/[educator_param]/getEducatorReviews';
import { ReviewsList } from './ReviewsList';
import { useCallback, useContext, useMemo, useState } from 'react';
import { LoadMoreButton } from './LoadMoreButton';
import { AppContext } from '../../../../contexts/AppContext';
import AddEducatorReviewLink from '../../../../components/AddEducatorReviewLink';

export type EducatorReviewsProps<Pages extends string> = {
    educator: PublicEducator;
    educatorReviews: GetEducatorReviewsResult;
} & {
    pages: EducatorProfileProps<Pages>['tabs'];
    activeTabKey: EducatorProfileProps<Pages>['activeTabKey'];
};

export const EducatorReviews = <Pages extends string>({
    educator,
    educatorReviews,
    pages,
    activeTabKey,
}: EducatorReviewsProps<Pages>) => {
    const appContext = useContext(AppContext);
    const reviewsContext = useMemo((): 'minisite' | 'marketplace' => {
        switch (appContext.context) {
            case 'embed':
                return 'minisite';
            case 'minisite':
                return 'minisite';
            case 'marketplace':
                return 'marketplace';
        }
    }, [appContext]);
    const [reviews, setReviews] = useState<PublicReview[]>(educatorReviews.reviews);
    const [nextPageToken, setNextPageToken] = useState<string | null>(
        educatorReviews.nextPageToken
    );

    const fetchMoreReviews = useCallback(async () => {
        if (!nextPageToken) {
            return;
        }
        const { reviews: newReviews, nextPageToken: newNextPageToken } = await getEducatorReviews(
            educator.slug,
            reviewsContext,
            nextPageToken
        );
        setReviews((reviews) => reviews.concat(newReviews));
        setNextPageToken(newNextPageToken || null);
    }, [nextPageToken, educator.slug, reviewsContext]);

    const hasMoreReviews = useMemo(() => {
        return Boolean(nextPageToken);
    }, [nextPageToken]);

    return (
        <EducatorProfile educator={educator} tabs={pages} activeTabKey={activeTabKey}>
            <main>
                <Container>
                    <div className={styles.container}>
                        <div className={styles.header}>
                            <h3>Reviews</h3>
                            <AddEducatorReviewLink
                                educatorSlug={educator.slug}
                                asButton={true}
                                preset="primary"
                            >
                                Add Review
                            </AddEducatorReviewLink>
                        </div>
                        <ReviewsList reviews={reviews} educator={educator} />
                        <LoadMoreButton onClick={fetchMoreReviews} hasMore={hasMoreReviews} />
                    </div>
                </Container>
            </main>
        </EducatorProfile>
    );
};
